import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { Avatar } from '@mui/material';
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import ErrorOutline from '@mui/icons-material/ErrorOutline';
import PhoneOutlined from '@mui/icons-material/PhoneOutlined';
import PhonePausedOutlined from '@mui/icons-material/PhonePausedOutlined';
import {
  CcfHeader,
  CcfHideTranscriptIcon,
  CcfPhoneInboundIcon,
  CcfPopOver,
  CcfTypography,
} from '@nice-ccf/ui-controls';
import {
  activeContactSelector,
  getActiveContactTranscript,
  updateActiveContact,
} from '../../ccf-inbox/ccf-inbox.slice';
import { selectLoginCredentialsState } from '../../login/+state/login.slice';
import { PopOverMenuItem } from '../../ccf-inbox/model/callControlPopover';
import { TranscriptMessage } from '../../ccf-inbox/model/transcript';
import headerStyles from '../ccf-interaction-space-header-tab/ccf-interaction-space-header-tab.module.scss';
import styles from './ccf-voice-transcript.module.scss';

/**
 * Returns the initials shown in a message avatar
 * @param name - display name
 * @example getInitials('Liam Davis') // 'LD'
 */
const getInitials = (name: string) =>
  name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join('');

/**
 * Returns the icon shown beside a call event line
 * @param eventType - kind of call event
 * @param iconUrl - uploaded image, used when eventType is 'custom'
 * @example getEventIcon('hold')
 */
const getEventIcon = (
  eventType: TranscriptMessage['eventType'],
  iconUrl?: string
) => {
  if (eventType === 'custom' && iconUrl) {
    return <img src={iconUrl} alt="" className={styles.eventImage} />;
  }
  switch (eventType) {
    case 'hold':
      return <PhonePausedOutlined className={styles.eventIcon} />;
    case 'error':
      return (
        <ErrorOutline className={`${styles.eventIcon} ${styles.eventError}`} />
      );
    case 'success':
      return (
        <CheckCircleOutline
          className={`${styles.eventIcon} ${styles.eventSuccess}`}
        />
      );
    default:
      return <PhoneOutlined className={styles.eventIcon} />;
  }
};

// Live playback timing
const NEXT_LINE_DELAY_MS = 700; // auto run: pause before the next line starts
const TYPING_INDICATOR_MS = 500; // auto run: "..." shown before a spoken line appears
const WORD_STREAM_MS = 20; // delay between words as a spoken line is written out

/**
 * Splits a message into words for the word-by-word writing effect
 * @param message - transcript line text
 * @example getWords('Hello there') // ['Hello', 'there']
 */
const getWords = (message?: string) =>
  (message ?? '').split(/\s+/).filter(Boolean);

/**
 * True when the key press belongs to a text field, so the Up arrow keeps its normal meaning there
 * @param target - element that received the key press
 * @example isTypingTarget(event.target)
 */
const isTypingTarget = (target: EventTarget | null) =>
  target instanceof HTMLElement &&
  (['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName) ||
    target.isContentEditable);

/**
 * Upper-cases the first letter and lower-cases the rest, like the app header does for the agent's name
 * @param value - name part
 * @example capitalize('sONAWANE') // 'Sonawane'
 */
const capitalize = (value?: string) =>
  value ? value.charAt(0).toUpperCase() + value.slice(1).toLowerCase() : '';

/**
 * Replaces the double-brace placeholders in a transcript line with the given values;
 * unknown placeholders are left as they are
 * @param text - transcript line text
 * @param values - placeholder values by name
 * @example fillPlaceholders(text, values)
 */
const fillPlaceholders = (
  text: string | undefined,
  values: Record<string, string>
) =>
  (text ?? '').replace(/\{\{(\w+)\}\}/g, (match, key) => values[key] ?? match);

/**
 * Read-only transcript of the voice call between the agent and the customer.
 * Shown in the middle container of the active voice contact after "Show Transcript" is clicked.
 * The messages come from the contact's own transcriptData, else from the initialTranscript in the store.
 * It plays like a live call: the first line is shown straight away. In manual control (the default) each
 * Up arrow key press shows the next line; in auto run (transcriptPlayback 'auto') the next line appears every
 * few seconds (a typing indicator, then the words written out one by one). Progress is kept on the contact,
 * and each call keeps its own place. Show and Hide Transcript reset it, so reopening replays from the first message.
 * @example <CcfVoiceTranscript />
 */
export function CcfVoiceTranscript() {
  const dispatch = useDispatch();
  const activeContact = useSelector(activeContactSelector);
  const storedTranscript = useSelector(getActiveContactTranscript);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const contactId = activeContact?.id;
  // Manual control is the default: the Up arrow key shows each next line. Auto run only when the journey
  // chose it in Simulator-v2
  const isManual = activeContact?.transcriptPlayback !== 'auto';

  // Logged-in agent, resolved the same way as the app header: SSO account name, else the login credentials
  const isAuthenticated = useIsAuthenticated();
  const { accounts } = useMsal();
  const loginCredentials = useSelector(selectLoginCredentialsState);
  const agentLastName = capitalize(
    isAuthenticated
      ? (accounts[0]?.name ?? '').trim().split(/\s+/)[1]
      : loginCredentials?.lastName
  );
  const agentName = agentLastName ? `Agent ${agentLastName}` : 'Agent';
  const customerFirstName = activeContact?.customer?.firstName || '';
  const customerName =
    [customerFirstName, activeContact?.customer?.lastName]
      .filter(Boolean)
      .join(' ') || 'Customer';
  // The transcript with {{agentName}}, {{customerName}} and {{customerFirstName}} filled in
  const transcript = useMemo(
    () =>
      storedTranscript.map((line) => ({
        ...line,
        message: fillPlaceholders(line.message, {
          agentName,
          customerName,
          customerFirstName: customerFirstName || customerName,
        }),
      })),
    [storedTranscript, agentName, customerName, customerFirstName]
  );
  // Always a whole number from 1 to the number of lines (0 only when there are no lines), whatever is saved
  const savedCount = activeContact?.transcriptRevealCount;
  const revealedCount =
    transcript.length === 0
      ? 0
      : Math.min(
          Math.max(
            typeof savedCount === 'number' && Number.isFinite(savedCount)
              ? Math.floor(savedCount)
              : 1,
            1
          ),
          transcript.length
        );
  // The spoken line that is about to appear, shown as a typing indicator
  const [typingFor, setTypingFor] = useState<TranscriptMessage | null>(null);
  // The spoken line currently being written out, and how many of its words are visible
  const [streaming, setStreaming] = useState<{
    index: number;
    words: number;
  } | null>(null);

  // A different call was selected, or the playback mode changed: drop any half-finished line
  useEffect(() => {
    setTypingFor(null);
    setStreaming(null);
  }, [contactId, isManual]);

  // Auto run: schedule the next line until the whole transcript has been revealed
  useEffect(() => {
    if (isManual || !contactId || revealedCount >= transcript.length) return;
    const next = transcript[revealedCount];
    if (!next) return;
    const isEvent = next.messageFrom === 'system';
    let typingTimer: ReturnType<typeof setTimeout> | undefined;

    /**
     * Shows the next line: starts writing it out and saves the new progress on the contact
     * @example reveal()
     */
    const reveal = () => {
      setTypingFor(null);
      setStreaming(isEvent ? null : { index: revealedCount, words: 0 });
      dispatch(
        updateActiveContact({
          id: contactId,
          updates: { transcriptRevealCount: revealedCount + 1 },
        })
      );
    };

    const waitTimer = setTimeout(() => {
      if (isEvent) {
        reveal();
        return;
      }
      setTypingFor(next);
      typingTimer = setTimeout(reveal, TYPING_INDICATOR_MS);
    }, NEXT_LINE_DELAY_MS);

    return () => {
      clearTimeout(waitTimer);
      clearTimeout(typingTimer);
    };
  }, [isManual, contactId, revealedCount, transcript, dispatch]);

  // Manual control: each Up arrow press shows the next line straight away (spoken lines are still written
  // out word by word). Ignored while typing in a field, with a modifier key held, for a held (repeating) key,
  // and when another component such as a menu already handled the key.
  useEffect(() => {
    if (!isManual || !contactId) return;
    /**
     * Shows the next transcript line when the Up arrow key is pressed
     * @param event - keyboard event
     * @example window.addEventListener('keydown', handleKeyDown)
     */
    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        event.key !== 'ArrowUp' ||
        event.altKey ||
        event.ctrlKey ||
        event.metaKey ||
        event.shiftKey ||
        // Holding the key repeats keydown; only a fresh press reveals a line
        event.repeat ||
        // Already handled elsewhere, e.g. arrow navigation inside a menu
        event.defaultPrevented ||
        isTypingTarget(event.target)
      ) {
        return;
      }
      const next = transcript[revealedCount];
      if (!next) return;
      // Stop the page or transcript from scrolling up on the same key press
      event.preventDefault();
      setTypingFor(null);
      setStreaming(
        next.messageFrom === 'system' ? null : { index: revealedCount, words: 0 }
      );
      dispatch(
        updateActiveContact({
          id: contactId,
          updates: { transcriptRevealCount: revealedCount + 1 },
        })
      );
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isManual, contactId, revealedCount, transcript, dispatch]);

  // Write the current spoken line out word by word
  useEffect(() => {
    if (!streaming) return;
    const item = transcript[streaming.index];
    if (!item || streaming.words >= getWords(item.message).length) {
      setStreaming(null);
      return;
    }
    const timer = setTimeout(
      () => setStreaming((s) => (s ? { ...s, words: s.words + 1 } : s)),
      WORD_STREAM_MS
    );
    return () => clearTimeout(timer);
  }, [streaming, transcript]);

  const dropdownOptions = {
    menuItems: [
      {
        items: [
          {
            label: 'Hide Transcript',
            icon: (
              <CcfHideTranscriptIcon
                style={{ fill: '#526b7a', height: '16px', width: '16px' }}
              />
            ),
            type: 'hideTranscript',
            closeOnSelection: true,
          },
        ],
      },
    ],
  };

  /**
   * Handles a selection from the header menu
   * @param item - selected menu item
   * @example onPopOverItemSelection(item)
   */
  const onPopOverItemSelection = (item: PopOverMenuItem) => () => {
    if (item.type === 'hideTranscript' && activeContact) {
      dispatch(
        updateActiveContact({
          id: activeContact.id,
          // Reset progress so showing the transcript again starts from the first message
          updates: { isShowTranscriptClicked: false, transcriptRevealCount: 1 },
        })
      );
    }
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView?.({ block: 'end' });
  }, [contactId, revealedCount, streaming?.words, typingFor]);

  return (
    <div className={styles.container}>
      <div className={styles.panel} id="agent_voiceTranscript">
        <CcfHeader
          RightIcon={false}
          HeaderWithTab={
            <div className={headerStyles.container}>
              <div className={headerStyles.header}>
                <div className={`${headerStyles.active} ${headerStyles.tab}`}>
                  <CcfPhoneInboundIcon className={styles.tabIcon} />
                  <div className={headerStyles.tabContent}>Voice</div>
                </div>
              </div>
            </div>
          }
          HeaderWithBookmark={
            <div className={headerStyles.bookmark}>
              <div className={headerStyles.bookmarkContent}>
                <div># {activeContact?.id}</div>
                <div style={{ flexGrow: 1 }}></div>
                <CcfPopOver
                  onPopOverItemSelection={onPopOverItemSelection}
                  optionList={dropdownOptions}
                />
              </div>
            </div>
          }
        />
        <div className={styles.messages}>
          {transcript.length === 0 && (
            <CcfTypography className={styles.empty}>
              No transcript available
            </CcfTypography>
          )}
          {transcript
            .slice(0, revealedCount)
            .map((item: TranscriptMessage, index: number) => {
            if (item.messageFrom === 'system') {
              return (
                <div className={styles.event} key={index}>
                  {getEventIcon(item.eventType, item.iconUrl)}
                  <span>{item.message}</span>
                  {item.time && <span>{item.time}</span>}
                </div>
              );
            }
            const isAgent = item.messageFrom === 'agent';
            const name = isAgent ? agentName : customerName;
            return (
              <div className={styles.message} key={index}>
                <Avatar
                  className={`${styles.avatar} ${
                    isAgent ? styles.agentAvatar : styles.customerAvatar
                  }`}
                >
                  {getInitials(name)}
                </Avatar>
                <div>
                  <div className={styles.meta}>
                    <span className={styles.sender}>{name}</span>
                    {item.time && <span className={styles.time}>{item.time}</span>}
                  </div>
                  <div className={styles.text}>
                    {streaming?.index === index
                      ? getWords(item.message).slice(0, streaming.words).join(' ')
                      : item.message}
                  </div>
                </div>
              </div>
            );
          })}
          {typingFor && (
            <div className={styles.message} aria-label="Transcribing">
              <Avatar
                className={`${styles.avatar} ${
                  typingFor.messageFrom === 'agent'
                    ? styles.agentAvatar
                    : styles.customerAvatar
                }`}
              >
                {getInitials(
                  typingFor.messageFrom === 'agent' ? agentName : customerName
                )}
              </Avatar>
              <div className={styles.typingDots}>
                <span />
                <span />
                <span />
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>
    </div>
  );
}

export default CcfVoiceTranscript;
